import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpen, CopyPlus, ExternalLink } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { useListingActions } from '../../../hooks/useListingAdmin';
import { useListing } from '../../../hooks/useListings';
import { CategoryKey, Listing } from '../../../types';
import { getCategory } from '../../../config/categories';
import { pickLang } from '../../../utils/listingDisplay';
import CategoryIcon from '../../directory/CategoryIcon';
import { ListingStatusTag } from '../../directory/StatusBadge';
import { listingHref } from '../../directory/ListingCard';
import ShulMinyanTabs from './ShulMinyanTabs';
import InfoTab from './InfoTab';
import DetailsTab from './DetailsTab';
import LocationTab from './LocationTab';
import ContactsTab from './ContactsTab';
import MessagesTab from './MessagesTab';
import HoursTab from './HoursTab';
import ShiurTimesTab from './ShiurTimesTab';

export type EditorTab =
  | 'info'
  | 'messages'
  | 'location'
  | 'contacts'
  | 'hours'
  | 'details'
  | 'times'
  | 'posters'
  | 'guidance'
  | 'shiurTimes';

/** Tabs per category (plan 11.1): shuls keep Times/Posters/Guidance; others get Hours. */
export function tabsFor(category: CategoryKey): EditorTab[] {
  const shared: EditorTab[] = ['info', 'messages', 'location', 'contacts'];
  if (category === 'shiur') return [...shared, 'details', 'shiurTimes'];
  return getCategory(category).usesHours
    ? [...shared, 'hours', 'details']
    : [...shared, 'details', 'times', 'posters', 'guidance'];
}

/** Old links used ?tab=shulInfo. */
function readTab(value: string | null, tabs: EditorTab[]): EditorTab {
  if (value === 'shulInfo') return 'info';
  return tabs.includes(value as EditorTab) ? (value as EditorTab) : 'info';
}

export interface TabProps {
  listing: Listing;
}

interface ListingEditorProps {
  listingId: string;
  /** Show "All my items" (the user has 2+ items, or is the super admin). */
  showBack: boolean;
}

export default function ListingEditor({ listingId, showBack }: ListingEditorProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const [searchParams, setSearchParams] = useSearchParams();
  const { listing, loading, error } = useListing(listingId);
  const { user } = useAuth();
  const { resubmit, saving } = useListingActions();
  const [resubmitFailed, setResubmitFailed] = React.useState(false);

  if (loading) return <p className="text-sm text-ash">{t('common.loading')}</p>;
  if (error) return <p className="text-sm text-danger-600">{error}</p>;
  // Only owners and the super admin edit (the rules refuse anyone else's saves anyway).
  const isSuper = user?.role === 'superAdmin';
  const canEdit = !!listing && (isSuper || (!!user && listing.ownerUids?.includes(user.uid)));
  // An owner's deleted listing is gone for them; only the super admin brings it back.
  const deletedForOwner = !!listing && listing.status === 'deleted' && !isSuper;
  if (!listing || !canEdit || deletedForOwner) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-ink">{t(deletedForOwner ? 'directory.admin.deleteOwn.gone' : 'directory.admin.notFound')}</p>
        <Link to="/admin?item=" className="text-sm text-ink underline">
          {t('directory.admin.backToItems')}
        </Link>
      </div>
    );
  }

  const tabs = tabsFor(listing.category);
  const tab = readTab(searchParams.get('tab'), tabs);
  const sub = 'subcategory' in listing ? listing.subcategory : undefined;

  const selectTab = (next: EditorTab) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params);
  };

  const handleResubmit = async () => {
    setResubmitFailed(false);
    try {
      await resubmit(listing.id);
    } catch {
      setResubmitFailed(true);
    }
  };

  const welcome = searchParams.get('added')
    ? t(listing.status === 'pending' ? 'directory.admin.add.thanksPending' : 'directory.admin.add.thanksDraft')
    : null;

  const banner =
    listing.status === 'pending'
      ? t('directory.admin.pendingBanner')
      : listing.status === 'draft'
        ? t('directory.admin.draftBanner')
        : listing.status === 'hidden'
          ? t('directory.admin.hiddenBanner')
          : null;

  return (
    <div className="space-y-4">
      {showBack && (
        <Link to="/admin?item=" className="text-sm text-ash hover:text-ink hover:underline">
          {isHebrew ? '→' : '←'} {t('directory.admin.backToItems')}
        </Link>
      )}

      <div className="flex items-start gap-3">
        <div className="shrink-0 w-12 h-12 rounded-[10px] bg-fog flex items-center justify-center text-ink/70">
          <CategoryIcon category={listing.category} className="w-6 h-6" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-ink break-words">
            {pickLang(listing.nameEn, listing.nameHe, isHebrew)}
          </h2>
          <p className="text-sm text-ash">
            {t(`directory.category.${listing.category}`)}
            {sub ? ` · ${t(`directory.sub.${sub}`)}` : ''}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <ListingStatusTag status={listing.status} />
            <Link
              to={listingHref(listing)}
              className="inline-flex items-center gap-1 text-sm text-ink hover:underline min-h-[44px]"
            >
              <ExternalLink className="w-4 h-4" aria-hidden />
              {t(listing.status === 'published' ? 'directory.admin.viewPublic' : 'directory.preview.viewButton')}
            </Link>
            <Link
              to={`/directory/add?from=${encodeURIComponent(listing.id)}`}
              className="inline-flex items-center gap-1 text-sm text-ink hover:underline min-h-[44px]"
            >
              <CopyPlus className="w-4 h-4" aria-hidden />
              {t('directory.admin.fillFrom.addAnother')}
            </Link>
            {listing.category === 'shul' && (
              <Link
                to={`/directory/add?category=shiur&shul=${encodeURIComponent(listing.id)}`}
                className="inline-flex items-center gap-1 text-sm text-ink hover:underline min-h-[44px]"
              >
                <BookOpen className="w-4 h-4" aria-hidden />
                {t('directory.shiur.addInShul')}
              </Link>
            )}
          </div>
        </div>
      </div>

      {welcome && (
        <div role="status" className="rounded-[16px] border border-ink bg-paper px-4 py-3 text-sm text-ink">
          {welcome}
        </div>
      )}

      {listing.status === 'rejected' && (
        <div role="status" className="rounded-[16px] border border-danger-600 bg-paper px-4 py-3 space-y-2">
          <p className="text-sm font-semibold text-ink">{t('directory.admin.rejected.title')}</p>
          {listing.rejectionReason ? (
            <p className="text-sm text-ink whitespace-pre-line">
              {t('directory.admin.rejected.reason')}: {listing.rejectionReason}
            </p>
          ) : null}
          <p className="text-sm text-ash">{t('directory.admin.rejected.help')}</p>
          <button
            type="button"
            onClick={handleResubmit}
            disabled={saving}
            className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-[10px] bg-ink text-paper text-sm font-medium disabled:opacity-40"
          >
            {t('directory.admin.rejected.sendAgain')}
          </button>
          {resubmitFailed && <p className="text-sm text-danger-600">{t('directory.admin.saveFailed')}</p>}
        </div>
      )}

      {banner && !welcome && (
        <div role="status" className="rounded-[16px] border border-ink/20 bg-fog px-4 py-3 text-sm text-ink">
          {banner}
        </div>
      )}

      <div
        role="tablist"
        className="no-scrollbar -mx-4 px-4 flex gap-2 overflow-x-auto border-b border-ink/10"
      >
        {tabs.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => selectTab(id)}
            className={`shrink-0 whitespace-nowrap min-h-[44px] px-3 text-sm border-b-2 -mb-px transition-colors ${
              tab === id ? 'border-ink text-ink font-semibold' : 'border-transparent text-ash hover:text-ink'
            }`}
          >
            {t(`directory.admin.tab.${id}`)}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        <TabContent tab={tab} listing={listing} />
      </div>
    </div>
  );
}

function TabContent({ tab, listing }: { tab: EditorTab; listing: Listing }) {
  switch (tab) {
    case 'times':
    case 'posters':
    case 'guidance':
      return <ShulMinyanTabs shulId={listing.id} tab={tab} />;
    case 'info':
      return <InfoTab key={listing.id} listing={listing} />;
    case 'location':
      // A shiur in a shul uses the shul's address; an online one has none.
      if (listing.category === 'shiur' && (listing.details?.where === 'shul' || listing.details?.where === 'online')) {
        return <ShiurLocationNote where={listing.details.where} />;
      }
      return <LocationTab key={listing.id} listing={listing} />;
    case 'messages':
      return <MessagesTab key={listing.id} listing={listing} />;
    case 'contacts':
      return <ContactsTab key={listing.id} listing={listing} />;
    case 'hours':
      return <HoursTab key={listing.id} listing={listing} />;
    case 'details':
      return <DetailsTab key={listing.id} listing={listing} />;
    case 'shiurTimes':
      return <ShiurTimesTab key={listing.id} listing={listing} />;
    default:
      return null;
  }
}

function ShiurLocationNote({ where }: { where: 'shul' | 'online' }) {
  const { t } = useTranslation();
  return (
    <p className="rounded-[16px] border border-ink/10 bg-fog px-4 py-3 text-sm text-ink">
      {t(where === 'shul' ? 'directory.shiur.locationFromShul' : 'directory.shiur.locationOnline')}
    </p>
  );
}
